import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { API_URL } from 'src/assets/http/apis';
import { IUserProfile, IUpdateProfileRequest, IApiResponse, IBillingUpdateRequest } from '../interfaces';
import { IRole, IGetRolesResponse } from 'src/app/shared/interfaces';
import { UnitSystem } from 'src/app/shared/enums';
import { ResponseStatus } from '../enums';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  private userProfileSubject = new BehaviorSubject<IUserProfile | null>(null);

  constructor(private http: HttpClient) {}

  /**
   * @description Gets the current user profile from the API
   * @returns {Observable<IUserProfile | null>} Observable of the user profile
   */
  getUserProfile(): Observable<IUserProfile | null> {
    if (this.userProfileSubject.value) {
      return this.userProfileSubject.asObservable();
    }

    const url = `${environment.apiUrl}${API_URL.USERS.GET_PROFILE}`;

    return this.http.get<IApiResponse<IUserProfile>>(url).pipe(
      map(response => {
        if (response.data) {
          this.userProfileSubject.next(response.data);
          return response.data;
        }
        return null;
      })
    );
  }

  /**
   * @description Updates the user profile
   * @param {IUpdateProfileRequest} profile - The profile data to update
   * @returns {Observable<boolean>} Observable indicating success/failure
   */
  updateProfile(profile: IUpdateProfileRequest): Observable<boolean> {
    const url = `${environment.apiUrl}${API_URL.USERS.PUT_PROFILE}`;

    return this.http.put<IApiResponse<object>>(url, profile).pipe(
      map(response => {
        if (response.status == ResponseStatus.SUCCESS) {
          const currentProfile = this.userProfileSubject.value;
          if (currentProfile) {
            const updatedProfile: IUserProfile = {
              ...currentProfile,
              language: profile.language,
              unitOfMeasurement: profile.unitOfMeasurement,
            };
            this.userProfileSubject.next(updatedProfile);
          }
          return true;
        }
        return false;
      })
    );
  }

  /**
   * @description Updates the data region for the current user's workspace.
   * Admin-only: affects all members of the workspace.
   * @param {string} dataRegion - The region identifier ("Switzerland" or "Germany")
   * @returns {Observable<boolean>} Observable indicating success/failure
   */
  updateWorkspaceRegion(dataRegion: string): Observable<boolean> {
    const url = `${environment.apiUrl}${API_URL.USERS.PUT_WORKSPACE_REGION}`;
    return this.http.put<IApiResponse<object>>(url, { dataRegion }).pipe(
      map(response => {
        if (response.status == ResponseStatus.SUCCESS) {
          const currentProfile = this.userProfileSubject.value;
          if (currentProfile) {
            this.userProfileSubject.next({ ...currentProfile, dataRegion });
          }
          return true;
        }
        return false;
      })
    );
  }

  /**
   * @description Updates the billing information for the current user.
   * All fields are optional — `null` or omitted values clear the column
   * server-side (PUT semantics). On success, patches the cached profile so
   * the Account tab re-renders with the new values without a refetch.
   * @param {IBillingUpdateRequest} dto - The billing fields to upsert
   * @returns {Observable<boolean>} Observable indicating success/failure
   */
  updateBilling(dto: IBillingUpdateRequest): Observable<boolean> {
    const url = `${environment.apiUrl}${API_URL.USERS.PUT_BILLING}`;
    return this.http.put<IApiResponse<object>>(url, dto).pipe(
      map(response => {
        if (response.status == ResponseStatus.SUCCESS) {
          const currentProfile = this.userProfileSubject.value;
          if (currentProfile) {
            this.userProfileSubject.next({ ...currentProfile, ...dto });
          }
          return true;
        }
        return false;
      })
    );
  }

  /**
   * @description Gets the current user profile as an observable
   * @returns {Observable<IUserProfile | null>} Observable of the user profile
   */
  getProfileObservable(): Observable<IUserProfile | null> {
    return this.userProfileSubject.asObservable();
  }

  /**
   * @description Clears the cached user profile so the next getUserProfile()
   * call re-fetches from the server. Must be called on logout and after any
   * mutation that changes profile fields the UI branches on (e.g. the
   * first-time set-password flow flipping hasPassword from false to true).
   * Without this, the in-memory BehaviorSubject survives logout/login cycles
   * and the UI keeps reading stale values.
   * @returns {void}
   */
  clearProfile(): void {
    this.userProfileSubject.next(null);
  }

  /**
   * @description Gets the current unit system from the user profile
   * @returns {number} The current unit system (1 = Conventional, 2 = SI)
   */
  getUnitSystem(): number {
    const profile = this.userProfileSubject.value;
    return profile?.unitOfMeasurement || UnitSystem.Conventional;
  }

  /**
   * @description Gets all available user roles from the API
   * @returns {Observable<IRole[]>} Observable of the roles array
   */
  getUserRoles(): Observable<IRole[]> {
    const url = `${environment.apiUrl}${API_URL.USERS.GET_ROLES}`;

    return this.http.get<IGetRolesResponse>(url).pipe(
      map(response => {
        if (response.data && Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      })
    );
  }

  /**
   * @description Checks if the current user is an Admin
   * @returns {boolean} True if the user is an Admin, false otherwise
   */
  isAdmin(): boolean {
    const profile = this.userProfileSubject.value;
    return profile?.userRole === 1; // Admin role value is 1
  }

  /**
   * @description Checks if the current user is a Viewer
   * @returns {boolean} True if the user is a Viewer, false otherwise
   */
  isViewer(): boolean {
    const profile = this.userProfileSubject.value;
    return profile?.userRole === 3; // Viewer role value is 3
  }
}
